<!--
  NextPax Rate Plan Modal Component

  2026-05-13: Added Ctrip (CTR342) to channelOptions.
  2026-01-09: Added custom pricing type selector for additional pricing options (e.g., breakfast)
  2026-01-08: Fixed Expedia channel code from EXP142 to EXP270, added meal plan fetching from API
  2025-12-26: Fixed channel policy selector to properly render selected value by populating
              channel_policies from stored channel_policy_id during form initialization.
              Fixed error handling to display NextPax sync errors to users using Toaster
  2025-12-22: Added property-picker component to allow listing selection for property-specific rate plans
  2025-07-28: Fixed Vue reactivity issues with channel policy loading using $set method
-->
<template>
  <v-card>
    <v-card-title>
      <span class="text-h5">{{ title }}</span>
    </v-card-title>

    <v-card-text>
      <v-container>
        <v-row>
          <!-- Basic Information -->
          <v-col cols="12">
            <v-divider class="mb-4"></v-divider>
            <h3 class="text-h6 mb-3">
              {{ $t('rate_plans.basic_information') }}
            </h3>
          </v-col>

          <v-col cols="12" sm="6">
            <v-text-field
              v-model="formData.name"
              :label="$t('rate_plans.name')"
              :rules="[v => !!v || $t('rate_plans.name_required')]"
              required
            />
          </v-col>

          <v-col cols="12" sm="6">
            <v-text-field
              v-model="formData.code"
              :label="$t('rate_plans.code')"
              :rules="[v => !!v || $t('rate_plans.code_required')]"
              required
            />
          </v-col>

          <v-col cols="12">
            <v-textarea
              v-model="formData.description"
              :label="$t('rate_plans.description')"
              rows="3"
              auto-grow
              clearable
              :hint="$t('rate_plans.description_hint')"
            />
          </v-col>

          <!-- Pricing Configuration -->
          <v-col cols="12">
            <v-divider class="mb-4"></v-divider>
            <h3 class="text-h6 mb-3">
              {{ $t('rate_plans.pricing_configuration') }}
            </h3>
          </v-col>

          <v-col cols="12" sm="6">
            <v-text-field
              v-model.number="formData.pricing_offset"
              :label="$t('rate_plans.pricing_offset')"
              type="number"
              :rules="[
                v => v !== null || $t('rate_plans.pricing_offset_required'),
                v =>
                  (v >= -100 && v <= 100) ||
                  $t('rate_plans.pricing_offset_range'),
              ]"
              required
              :hint="$t('rate_plans.pricing_offset_hint')"
              persistent-hint
            />
          </v-col>

          <v-col cols="12" sm="6">
            <v-select
              v-model="formData.pricing_type"
              :items="pricingTypeOptions"
              :label="$t('rate_plans.pricing_type')"
              item-text="text"
              item-value="value"
              :hint="$t('rate_plans.pricing_type_hint')"
              persistent-hint
            />
          </v-col>

          <v-col cols="12" sm="3">
            <v-select
              v-model="formData.extra_data.custom_pricing_type"
              :items="customPricingTypeOptions"
              :label="$t('rate_plans.custom_pricing_type')"
              item-text="text"
              item-value="value"
              clearable
              :hint="$t('rate_plans.custom_pricing_type_hint')"
              persistent-hint
              @change="onCustomPricingTypeChange"
            />
          </v-col>

          <v-col
            v-if="formData.extra_data.custom_pricing_type"
            cols="12"
            sm="3"
          >
            <v-text-field
              v-model.number="formData.extra_data.custom_pricing_amount"
              type="number"
              min="0"
              step="0.01"
              :label="$t('rate_plans.custom_pricing_amount')"
              :hint="$t('rate_plans.custom_pricing_amount_hint')"
              persistent-hint
            />
          </v-col>

          <!-- NextPax Specific Fields -->
          <v-col cols="12">
            <v-divider class="mb-4"></v-divider>
            <h3 class="text-h6 mb-3">
              {{ $t('rate_plans.nextpax_configuration') }}
            </h3>
          </v-col>

          <v-col cols="12" sm="6">
            <v-text-field
              v-model.number="formData.policy_id"
              :label="$t('rate_plans.policy_id')"
              type="number"
              :hint="$t('rate_plans.policy_id_hint')"
              persistent-hint
            />
          </v-col>

          <v-col cols="12" sm="6">
            <v-select
              v-model="formData.meal_plan"
              :items="mealPlanOptions"
              :label="$t('rate_plans.meal_plan')"
              item-text="text"
              item-value="value"
              :hint="$t('rate_plans.meal_plan_hint')"
              persistent-hint
            />
          </v-col>

          <v-col cols="12" sm="6">
            <v-switch
              v-model="formData.tax_included"
              :label="$t('rate_plans.tax_included')"
              :hint="$t('rate_plans.tax_included_hint')"
              persistent-hint
            />
          </v-col>

          <!-- Channel Configuration -->
          <v-col cols="12">
            <v-divider class="mb-4"></v-divider>
            <h3 class="text-h6 mb-3">
              {{ $t('rate_plans.channel_configuration') }}
            </h3>
          </v-col>

          <v-col cols="12">
            <v-select
              v-model="formData.applicable_channels"
              :items="channelOptions"
              :label="$t('rate_plans.applicable_channels')"
              item-text="text"
              item-value="value"
              multiple
              chips
              :rules="[v => v.length > 0 || $t('rate_plans.channels_required')]"
              required
              :hint="$t('rate_plans.applicable_channels_hint')"
              persistent-hint
              @change="onChannelsChange"
            />
          </v-col>

          <!-- Dynamic Channel Policy Selection -->
          <v-col v-if="formData.applicable_channels.length > 0" cols="12">
            <v-expansion-panels>
              <v-expansion-panel
                v-for="channel in formData.applicable_channels"
                :key="channel"
              >
                <v-expansion-panel-header>
                  {{
                    $t('rate_plans.channel_policy_for', {
                      channel: getChannelName(channel),
                    })
                  }}
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-select
                    v-model="formData.channel_policies[channel]"
                    :label="$t('rate_plans.channel_policy_label')"
                    :items="getChannelPolicies(channel)"
                    item-text="description"
                    item-value="id"
                    outlined
                    dense
                    :loading="loadingPolicies[channel]"
                    :hint="$t('rate_plans.channel_policy_hint')"
                    persistent-hint
                  />
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-col>

          <!-- Date Configuration -->
          <v-col cols="12">
            <v-divider class="mb-4"></v-divider>
            <h3 class="text-h6 mb-3">
              {{ $t('rate_plans.date_configuration') }}
            </h3>
          </v-col>

          <v-col cols="12" sm="6">
            <v-menu
              v-model="validFromMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template #activator="{ on, attrs }">
                <v-text-field
                  v-model="formData.valid_from_date"
                  :label="$t('rate_plans.valid_from_date')"
                  readonly
                  v-bind="attrs"
                  :hint="$t('rate_plans.valid_from_date_hint')"
                  persistent-hint
                  v-on="on"
                />
              </template>
              <v-date-picker
                v-model="formData.valid_from_date"
                @input="validFromMenu = false"
              />
            </v-menu>
          </v-col>

          <v-col cols="12" sm="6">
            <v-menu
              v-model="validUntilMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template #activator="{ on, attrs }">
                <v-text-field
                  v-model="formData.valid_until_date"
                  :label="$t('rate_plans.valid_until_date')"
                  readonly
                  v-bind="attrs"
                  :hint="$t('rate_plans.valid_until_date_hint')"
                  persistent-hint
                  v-on="on"
                />
              </template>
              <v-date-picker
                v-model="formData.valid_until_date"
                @input="validUntilMenu = false"
              />
            </v-menu>
          </v-col>

          <!-- Arrival Date Configuration -->
          <v-col cols="12" sm="6">
            <v-menu
              v-model="arrivalsFromMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template #activator="{ on, attrs }">
                <v-text-field
                  v-model="formData.arrivals_from_date"
                  :label="$t('rate_plans.arrivals_from_date')"
                  readonly
                  v-bind="attrs"
                  :hint="$t('rate_plans.arrivals_from_date_hint')"
                  persistent-hint
                  v-on="on"
                />
              </template>
              <v-date-picker
                v-model="formData.arrivals_from_date"
                @input="arrivalsFromMenu = false"
              />
            </v-menu>
          </v-col>

          <v-col cols="12" sm="6">
            <v-menu
              v-model="arrivalsUntilMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template #activator="{ on, attrs }">
                <v-text-field
                  v-model="formData.arrivals_until_date"
                  :label="$t('rate_plans.arrivals_until_date')"
                  readonly
                  v-bind="attrs"
                  :hint="$t('rate_plans.arrivals_until_date_hint')"
                  persistent-hint
                  v-on="on"
                />
              </template>
              <v-date-picker
                v-model="formData.arrivals_until_date"
                @input="arrivalsUntilMenu = false"
              />
            </v-menu>
          </v-col>

          <!-- Stay Restrictions -->
          <v-col cols="12">
            <v-divider class="mb-4"></v-divider>
            <h3 class="text-h6 mb-3">
              {{ $t('rate_plans.stay_restrictions') }}
            </h3>
          </v-col>

          <v-col cols="12" sm="4">
            <v-text-field
              v-model.number="formData.minimum_length_of_stay"
              :label="$t('rate_plans.minimum_length_of_stay')"
              type="number"
              min="1"
              :hint="$t('rate_plans.minimum_length_of_stay_hint')"
              persistent-hint
            />
          </v-col>

          <v-col cols="12" sm="4">
            <v-text-field
              v-model.number="formData.max_days_before_arrival"
              :label="$t('rate_plans.max_days_before_arrival')"
              type="number"
              min="1"
              :hint="$t('rate_plans.max_days_before_arrival_hint')"
              persistent-hint
            />
          </v-col>

          <v-col cols="12" sm="4">
            <v-text-field
              v-model.number="formData.min_days_before_arrival"
              :label="$t('rate_plans.min_days_before_arrival')"
              type="number"
              min="0"
              :hint="$t('rate_plans.min_days_before_arrival_hint')"
              persistent-hint
            />
          </v-col>

          <!-- Property Settings -->
          <v-col cols="12">
            <v-divider class="mb-4"></v-divider>
            <h3 class="text-h6 mb-3">
              {{ $t('rate_plans.property_settings') }}
            </h3>
          </v-col>

          <v-col cols="12" sm="6">
            <v-switch
              v-model="formData.is_property_specific"
              :label="$t('rate_plans.is_property_specific')"
              :hint="$t('rate_plans.is_property_specific_hint')"
              persistent-hint
            />
          </v-col>

          <v-col cols="12" sm="6">
            <v-switch
              v-model="formData.custom_default"
              :label="$t('rate_plans.custom_default')"
              :hint="$t('rate_plans.custom_default_hint')"
              persistent-hint
            />
          </v-col>

          <v-col cols="12">
            <property-picker
              v-if="formData.is_property_specific"
              :after-change="applicableListingsChanged"
              :value="formData.listing_ids"
              :label="$t('rate_plans.applicable_listings')"
              :allow-multiple="true"
              :clearable="true"
            />
          </v-col>

          <!-- Restrictions Management -->
          <v-col cols="12">
            <v-divider class="mb-4"></v-divider>
            <h3 class="text-h6 mb-3">
              {{ $t('rate_plans.restrictions_management') }}
            </h3>
            <p class="text-body-2 text--secondary mb-4">
              {{ $t('rate_plans.restrictions_management_hint') }}
            </p>
            <v-btn color="primary" outlined small @click="addRestriction">
              <v-icon left>mdi-plus</v-icon>
              {{ $t('rate_plans.add_restriction') }}
            </v-btn>
          </v-col>

          <v-col cols="12">
            <v-expansion-panels>
              <v-expansion-panel
                v-for="(restriction, index) in formData.restrictions"
                :key="index"
              >
                <v-expansion-panel-header>
                  {{ $t('rate_plans.restriction', { number: index + 1 }) }}
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <restriction-form
                    v-model="formData.restrictions[index]"
                    @remove="removeRestriction(index)"
                  />
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-col>
        </v-row>
      </v-container>
    </v-card-text>

    <v-card-actions>
      <v-spacer />
      <v-btn color="grey darken-1" text @click="closeModal"> Cancel </v-btn>
      <v-btn
        color="primary"
        :loading="loading"
        :disabled="!isFormValid"
        @click="saveRatePlan"
      >
        {{ isEditing ? 'Update' : 'Create' }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import RestrictionForm from './restriction-form.vue'
import PropertyPicker from 'components/property-picker.vue'
import Toaster from '@/utils/toaster'
import axios from 'axios'

export default {
  name: 'NextpaxRatePlanModal',

  components: {
    RestrictionForm,
    PropertyPicker,
  },

  props: {
    ratePlan: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      loading: false,
      validFromMenu: false,
      validUntilMenu: false,
      arrivalsFromMenu: false,
      arrivalsUntilMenu: false,
      loadingPolicies: {},
      channelPolicies: {},
      formData: {
        name: '',
        code: '',
        description: '',
        pricing_type: 'default',
        pricing_offset: null,
        channel_policy_id: '',
        policy_id: '',
        meal_plan: 'default',
        tax_included: false,
        applicable_channels: [],
        channel_policies: {},
        valid_from_date: '',
        valid_until_date: '',
        arrivals_from_date: '',
        arrivals_until_date: '',
        minimum_length_of_stay: null,
        max_days_before_arrival: null,
        min_days_before_arrival: null,
        is_property_specific: false,
        custom_default: false,
        pms_type: 'nextpax',
        restrictions: [],
        listing_ids: [],
        extra_data: {
          custom_pricing_type: null,
          custom_pricing_amount: null,
        },
      },
      pricingTypeOptions: [
        { text: 'Default', value: 'default' },
        { text: 'Dynamic', value: 'dynamic' },
        { text: 'Fixed', value: 'fixed' },
      ],
      customPricingTypeOptions: [{ text: 'Breakfast', value: 'breakfast' }],
      // Legacy fixed presets map to the editable 'breakfast' category + amount
      legacyPricingAmounts: { breakfast_30: 30, breakfast_45: 45 },
      mealPlanOptions: [{ text: 'Default', value: 'default' }],
      channelOptions: [
        { text: 'Booking.com', value: 'BOO142' },
        { text: 'Agoda', value: 'AGO304' },
        { text: 'Expedia', value: 'EXP270' },
        { text: 'Hotels.com', value: 'HOT142' },
        { text: 'Trip.com', value: 'TRP142' },
        { text: 'Ctrip', value: 'CTR342' },
      ],
    }
  },

  computed: {
    ...mapState('ratePlan', ['availableListings']),

    title() {
      return this.isEditing
        ? this.$t('rate_plans.edit_nextpax_rate_plan')
        : this.$t('rate_plans.create_nextpax_rate_plan')
    },

    isEditing() {
      return !!this.ratePlan
    },

    isFormValid() {
      return (
        this.formData.name &&
        this.formData.code &&
        this.formData.applicable_channels.length > 0
      )
    },
  },

  watch: {
    ratePlan: {
      handler(newRatePlan) {
        if (newRatePlan) {
          this.initializeForm()
        }
      },
      immediate: true,
    },
  },

  async created() {
    await this.fetchMealPlans()
  },

  methods: {
    ...mapActions('ratePlan', ['createRatePlan', 'updateRatePlan']),

    async fetchMealPlans() {
      try {
        const response = await axios.get('/api/rate-plans/meal-plans')
        const mealPlans =
          response.data.payload?.data?.meal_plans ||
          response.data?.meal_plans ||
          []
        // Keep the default option and add NextPax meal plans
        this.mealPlanOptions = [
          { text: 'Default', value: 'default' },
          ...mealPlans,
        ]
      } catch (error) {
        console.error('Error fetching meal plans:', error)
      }
    },

    initializeForm() {
      if (this.ratePlan) {
        // Build channel_policies from the stored channel_policy_id
        // The DB stores a single channel_policy_id, map it to the first applicable channel
        const channelPolicies = this.ratePlan.channel_policies || {}
        if (
          this.ratePlan.channel_policy_id &&
          this.ratePlan.applicable_channels?.length > 0
        ) {
          // Ensure the policy ID is stored as string for proper v-select matching
          const firstChannel = this.ratePlan.applicable_channels[0]
          channelPolicies[firstChannel] = String(
            this.ratePlan.channel_policy_id
          )
        }

        const extraData = this.ratePlan.extra_data || {}
        const rawPricingType = extraData.custom_pricing_type || null
        const legacyAmount = this.legacyPricingAmounts[rawPricingType]
        const isLegacy = legacyAmount != null
        this.formData = {
          ...this.formData,
          ...this.ratePlan,
          channel_policies: channelPolicies,
          listing_ids: this.ratePlan.listings?.map(l => l.id) || [],
          extra_data: {
            ...extraData,
            custom_pricing_type: isLegacy ? 'breakfast' : rawPricingType,
            custom_pricing_amount:
              extraData.custom_pricing_amount ??
              (isLegacy ? legacyAmount : null),
          },
        }

        // Load policies for existing channels
        this.loadPoliciesForChannels(this.formData.applicable_channels)
      }
    },

    onCustomPricingTypeChange(value) {
      // Clear the amount when the custom pricing type is removed
      if (!value) this.formData.extra_data.custom_pricing_amount = null
    },

    applicableListingsChanged(value) {
      this.formData.listing_ids = value
    },

    async onChannelsChange() {
      console.log('Channels changed to:', this.formData.applicable_channels)

      // Load policies for newly selected channels
      const newChannels = this.formData.applicable_channels.filter(
        channel => !this.channelPolicies[channel]
      )

      console.log('New channels to load policies for:', newChannels)

      if (newChannels.length > 0) {
        await this.loadPoliciesForChannels(newChannels)
      }

      // Debug all channel states
      this.formData.applicable_channels.forEach(channel => {
        this.debugChannelState(channel)
      })
    },

    async loadPoliciesForChannels(channels) {
      for (const channel of channels) {
        await this.loadChannelPolicies(channel)
      }
    },

    async loadChannelPolicies(channel) {
      this.$set(this.loadingPolicies, channel, true)

      try {
        const response = await axios.get('/api/rate-plans/channel-policies', {
          params: { channels: [channel] },
        })

        if (response.data.success) {
          // Handle the nested response structure: payload.data.channel_policies
          const channelPolicies =
            response.data.payload?.data?.channel_policies || {}
          this.$set(
            this.channelPolicies,
            channel,
            channelPolicies[channel] || []
          )
        }
      } catch (error) {
        console.error('Error loading channel policies:', error)
        this.$set(this.channelPolicies, channel, [])
        this.$toast.error(this.$t('rate_plans.error_loading_policies'))
      } finally {
        this.$set(this.loadingPolicies, channel, false)
      }
    },

    getChannelName(channelCode) {
      const channel = this.channelOptions.find(c => c.value === channelCode)
      return channel ? channel.text : channelCode
    },

    getChannelPolicies(channel) {
      const policies = this.channelPolicies[channel] || []
      console.log(`Getting policies for ${channel}:`, policies)
      return policies
    },

    // Debug method to check loading states
    debugChannelState(channel) {
      console.log(`Channel ${channel} state:`, {
        loading: this.loadingPolicies[channel],
        policies: this.channelPolicies[channel],
        policiesCount: (this.channelPolicies[channel] || []).length,
      })
    },

    addRestriction() {
      this.formData.restrictions.push({
        fromDate: '',
        untilDate: '',
        availabilityStatus: 'open',
        arrivalsAllowed: true,
        departuresAllowed: true,
        minimumStay: null,
        maximumStay: null,
        minimumStayThrough: null,
        maximumStayThrough: null,
        maxDaysBeforeArrival: null,
        minDaysBeforeArrival: null,
      })
    },

    removeRestriction(index) {
      this.formData.restrictions.splice(index, 1)
    },

    async saveRatePlan() {
      if (!this.isFormValid) {
        return
      }

      this.loading = true

      try {
        const ratePlanData = {
          ...this.formData,
          // Convert channel_policies object to channel_policy_id for the first selected channel
          channel_policy_id:
            this.formData.applicable_channels.length > 0
              ? this.formData.channel_policies[
                  this.formData.applicable_channels[0]
                ]
              : null,
        }

        if (this.isEditing) {
          await this.updateRatePlan({
            id: this.ratePlan.id,
            ratePlan: ratePlanData,
          })
        } else {
          await this.createRatePlan({
            ratePlan: ratePlanData,
          })
        }

        // Only close modal on success
        Toaster.show([
          {
            type: 'success',
            text: this.isEditing
              ? this.$t('rate_plans.rate_plan_updated')
              : this.$t('rate_plans.rate_plan_created'),
          },
        ])
        this.loading = false
        this.closeModal()
      } catch (error) {
        console.error('Error saving rate plan:', error)
        const errorMessage =
          error?.response?.data?.payload?.data?.error ||
          error?.response?.data?.error ||
          error?.message ||
          this.$t('rate_plans.error_saving_rate_plan')

        Toaster.show([{ type: 'error', text: errorMessage }])
        this.loading = false
        // Don't close modal on error - allow user to fix and retry
      }
    },

    closeModal() {
      if (this.loading) {
        return
      }
      this.$store.commit('hideModal')
    },
  },
}
</script>
